import type { CrewMember } from './types.js';

export type CrewFilter = 'all' | 'on_board' | 'ashore' | 'not_assigned';
/** "Login" süzgeci (ortak 2026-10-30): hepsi ya da yalnız hesapsız kişiler. */
export type CrewLoginFilter = 'all' | 'no_login';
export type CrewLoginState = 'login' | 'none' | 'disabled';

/**
 * "Login" sütununun rozeti (ortak 2026-10-30): hesabı yok, devre dışı ya da etkin. Eski sunucu hesap
 * durumunu göndermezse hesap etkin sayılır.
 */
export function loginState(member: Pick<CrewMember, 'hasLogin' | 'loginActive'>): CrewLoginState {
  if (member.hasLogin === false) return 'none';
  return member.loginActive === false ? 'disabled' : 'login';
}

/**
 * "+ Add Crew" (ortak 2026-10-30): kart yazabilen kaynakta ve sunucu `users.create` verdiyse; eski
 * sunucu `canCreate` göndermezse düğme görünür (yetkiyi sunucu yine denetler).
 */
export function canAddCrew(hasEditor: boolean, list: { canCreate?: boolean }): boolean {
  return hasEditor && list.canCreate !== false;
}
export type CrewSort = 'rank' | 'name' | 'status' | 'days' | 'contract';
export type SortDir = 'asc' | 'desc';

/** Uzun süredir karada sayılan gün (ofis için uyarı rengi; ADR 0012 §5). */
export const LONG_ASHORE_DAYS = 90;
export const CREW_PAGE_SIZE = 20;

export interface CrewSummary {
  total: number;
  onBoard: number;
  ashore: number;
  longAshore: number;
}

export function summarizeCrew(crew: readonly CrewMember[]): CrewSummary {
  const ashore = crew.filter((member) => member.status === 'ashore');
  return {
    total: crew.length,
    // Yalnız kartı açılmış kişi (ataması yok) gemide sayılmaz (ADR 0131 §1)
    onBoard: crew.filter((member) => member.status === 'on_board').length,
    ashore: ashore.length,
    longAshore: ashore.filter((member) => (member.days ?? 0) > LONG_ASHORE_DAYS).length,
  };
}

/** Kontrat rozetinin tonu, metin anahtarı ve gün sayısı (ADR 0076 §5). */
export interface ContractBadge {
  tone: 'neutral' | 'warning' | 'danger';
  key:
    | 'crew.contract.left'
    | 'crew.contract.today'
    | 'crew.contract.overdue'
    | 'crew.contract.mlcOverdue';
  count: number;
}

/**
 * Kalan gün rozeti: ≤ 60 gün sarı, geçmişse kırmızı ("Contract ended" ya da "MLC limit
 * exceeded"); kontrat yoksa (karada) rozet yok.
 */
export function contractBadge(contract: CrewMember['contract']): ContractBadge | null {
  if (!contract) return null;
  if (contract.state === 'overdue') {
    return {
      tone: 'danger',
      key: contract.basis === 'mlc' ? 'crew.contract.mlcOverdue' : 'crew.contract.overdue',
      count: -contract.daysLeft,
    };
  }
  return {
    tone: contract.state === 'warning' ? 'warning' : 'neutral',
    key: contract.daysLeft === 0 ? 'crew.contract.today' : 'crew.contract.left',
    count: contract.daysLeft,
  };
}

/**
 * Rütbe kıdemi, en kıdemliden (M6f G2-01; soru #92, ajan kararı ADR 0033 eki). Veri modelinde
 * kıdem yok: rol yalnız ad taşır ve adı şirket verir. Liste STCW'nin yönetim, operasyon ve
 * destek düzeylerini izler [STCW II/2, III/2, II/1, III/1, II/4, III/4]: önce kaptan, birinci
 * zabit ve başmühendis; sonra ikinci, üçüncü zabit ve mühendisler; sonra tayfa ve otel bölümü.
 * Yalnız görüntü sırasıdır; kayıt değiştirmez. Her satır eş anlamlıları taşır (küçük harf).
 */
export const RANK_ORDER: readonly (readonly string[])[] = [
  ['master', 'captain', 'kaptan'],
  ['chief officer', 'chief mate', 'first officer', 'birinci zabit', 'ikinci kaptan'],
  ['chief engineer', 'başmühendis', 'baş mühendis'],
  ['2nd officer', 'second officer', 'second mate', '2nd mate', 'ikinci zabit'],
  ['2nd engineer', 'second engineer', 'ikinci mühendis'],
  ['3rd officer', 'third officer', 'third mate', '3rd mate', 'üçüncü zabit'],
  ['3rd engineer', 'third engineer', 'üçüncü mühendis'],
  ['deck officer', 'officer of the watch', 'güverte zabiti', 'vardiya zabiti'],
  ['engineer officer', 'engineer', 'makine zabiti', 'mühendis'],
  ['electro-technical officer', 'eto', 'electrical officer', 'elektrik zabiti'],
  ['electrician', 'elektrikçi'],
  ['deck cadet', 'güverte stajyeri'],
  ['engine cadet', 'makine stajyeri'],
  ['bosun', 'boatswain', 'lostromo'],
  ['ab', 'able seaman', 'able bodied seaman', 'usta gemici'],
  ['os', 'ordinary seaman', 'gemici'],
  ['deckhand', 'güverte tayfası'],
  ['fitter', 'motorman', 'pumpman', 'oiler', 'yağcı'],
  ['wiper', 'silici'],
  // Chief Cook / Chief Steward sistem rütbesi değil (ADR 0139 §2); şirketin kendi rolü ya da korunan
  // eski rol bu adlarla kalabilir: sıra yalnız görüntü içindir, satırlar kalır
  ['chief stewardess', 'chief steward', 'baş kamarot'],
  ['stewardess', 'steward', 'kamarot'],
  ['chief cook', 'chef', 'aşçıbaşı'],
  ['cook', 'aşçı'],
  ['messman', 'mess boy', 'cleaner', 'temizlik görevlisi'],
];

const RANK_INDEX = new Map(
  RANK_ORDER.flatMap((names, index) => names.map((name) => [name, index] as const)),
);

/** Rütbenin kıdem sırası (0 en kıdemli); listede yoksa `null`. */
export function rankIndex(role: string | null | undefined): number | null {
  if (!role) return null;
  return RANK_INDEX.get(role.trim().replace(/\s+/g, ' ').toLocaleLowerCase('tr')) ?? null;
}

/**
 * Personel listelerinin varsayılan sırası (plan §0.6): rütbe kıdemi, sonra ad. Listede olmayan
 * rütbeler kıdemlilerden sonra, rütbe adına göre alfabetik; rütbesizler en sonda.
 */
export function compareByRank(
  a: { fullName: string; role: string | null },
  b: { fullName: string; role: string | null },
  locale = 'en',
): number {
  const x = rankIndex(a.role);
  const y = rankIndex(b.role);
  const byName = a.fullName.localeCompare(b.fullName, locale);
  if (x !== null && y !== null) return x - y || byName;
  if (x !== null) return -1;
  if (y !== null) return 1;
  if (a.role && b.role) return a.role.localeCompare(b.role, locale) || byName;
  if (a.role) return -1;
  if (b.role) return 1;
  return byName;
}

/**
 * Filtre, arama ve sıralama. Varsayılan sıra rütbe kıdemi (`rank`). `desc` sütunun sırasını
 * çevirir; günü bilinmeyen kişi her iki yönde de sonda. Eşitlikte ad sırası.
 */
export function viewCrew(
  crew: readonly CrewMember[],
  options: {
    filter: CrewFilter;
    query: string;
    sort: CrewSort;
    dir?: SortDir;
    locale: string;
    /** Gemi (gemide ya da son ayrıldığı); `null` hepsi. */
    vesselId?: string | null;
    rank?: string | null;
    login?: CrewLoginFilter;
  },
): CrewMember[] {
  const query = options.query.trim().toLocaleLowerCase(options.locale);
  const sign = options.dir === 'desc' ? -1 : 1;
  const byName = (x: CrewMember, y: CrewMember) =>
    x.fullName.localeCompare(y.fullName, options.locale);
  const compare: Record<CrewSort, (x: CrewMember, y: CrewMember) => number> = {
    rank: (x, y) => sign * compareByRank(x, y, options.locale),
    name: (x, y) => sign * byName(x, y),
    status: (x, y) => sign * x.status.localeCompare(y.status) || byName(x, y),
    days: (x, y) =>
      x.days === null || y.days === null
        ? (x.days === null ? 1 : 0) - (y.days === null ? 1 : 0) || byName(x, y)
        : sign * (x.days - y.days) || byName(x, y),
    // Kontratı olmayan (karadaki) her iki yönde de sonda
    contract: (x, y) => {
      const a = x.contract?.daysLeft ?? null;
      const b = y.contract?.daysLeft ?? null;
      if (a === null || b === null)
        return (a === null ? 1 : 0) - (b === null ? 1 : 0) || byName(x, y);
      return sign * (a - b) || byName(x, y);
    },
  };
  return crew
    .filter((member) => options.filter === 'all' || member.status === options.filter)
    .filter((member) => !options.vesselId || member.vessel?.id === options.vesselId)
    .filter((member) => !options.rank || member.role === options.rank)
    .filter((member) => options.login !== 'no_login' || loginState(member) === 'none')
    .filter(
      (member) =>
        !query ||
        [
          member.fullName,
          member.role ?? '',
          member.vessel?.name ?? '',
          member.crewCode ?? '',
          member.nationality ?? '',
        ]
          .join(' ')
          .toLocaleLowerCase(options.locale)
          .includes(query),
    )
    .sort(compare[options.sort]);
}

/** Rütbe süzgecinin seçenekleri: listedeki rütbeler, kıdem sırasıyla (G2-01). */
export function crewRanks(crew: readonly CrewMember[], locale: string): string[] {
  return [
    ...new Set(crew.map((member) => member.role).filter((role): role is string => !!role)),
  ].sort((x, y) => compareByRank({ fullName: '', role: x }, { fullName: '', role: y }, locale));
}

export function pageOf<T>(items: readonly T[], page: number, size = CREW_PAGE_SIZE): T[] {
  return items.slice((page - 1) * size, page * size);
}
